import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';

import {
  MattersTypes,
  MatterTypeResponse,
  MatterTypeRequest,
} from '../interfaces/matterTypes.interface';

export const mattersTypesTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['MattersTypes', 'MattersTypesAvailables'],
});
// Define a service using a base URL and expected endpoints
export const mattersTypesApi = mattersTypesTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all matters types
     */
    getMattersTypes: builder.query<
      MatterTypeResponse,
      {
        profileId: number;
        page: number;
        perPage: number;
        search: string;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async ({ profileId, page, search, perPage, sort, type_sort }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<MatterTypeResponse>('/type-subjects', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort: type_sort || 'asc',
              sort: sort || 'tm_name',
              search,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'MattersTypes', id } as const)),
              { type: 'MattersTypes', id: 'LIST' },
            ]
          : [{ type: 'MattersTypes', id: 'LIST' }],
    }),

    /**
     * @GET matter type by id
     */
    getMatterType: builder.query<MattersTypes, { profileId: number; matterTypeId: number }>({
      queryFn: async ({ profileId, matterTypeId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<MattersTypes>('/type-subjects', {
            params: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'MattersTypes', profileId }],
    }),

    getAvailablesMatterType: builder.query<MattersTypes[], { profileId: number; offerId?: number }>(
      {
        queryFn: async ({ profileId, offerId }) => {
          try {
            const { data } = await mainApi.get<MattersTypes[]>('/type-subjects', {
              params: {
                user_profile_id: profileId,
                data: 'all',
                ...(offerId && { offer_id: offerId }),
              },
            });

            return { data: Array.isArray(data) ? data : [] };
          } catch (error: any) {
            return { error };
          }
        },
        providesTags: (result) => [
          {
            type: 'MattersTypesAvailables',
            id: 'COMBO_LIST',
          },
        ],
      }
    ),

    /**
     * @POST create matter type
     */
    addMatterType: builder.mutation<
      MatterTypeRequest,
      { profileId: number; matterTypePayload: Partial<MatterTypeRequest> }
    >({
      queryFn: async ({ profileId, matterTypePayload }) => {
        try {
          const { data } = await mainApi.post<MatterTypeRequest>('/type-subjects', {
            user_profile_id: profileId,
            ...matterTypePayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'MattersTypes', id: 'LIST' },
        { type: 'MattersTypesAvailables', id: 'COMBO_LIST' },
      ],
    }),

    /**
     * @PUT update matter type
     */
    updateMatterType: builder.mutation<
      MatterTypeRequest,
      { profileId: number; matterTypeId: number; matterTypePayload: Partial<MatterTypeRequest> }
    >({
      queryFn: async ({ profileId, matterTypeId, matterTypePayload }) => {
        try {
          const { data } = await mainApi.put<MatterTypeRequest>(`/type-subjects/${matterTypeId}`, {
            user_profile_id: profileId,
            ...matterTypePayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'MattersTypes', id: 'LIST' },
        { type: 'MattersTypesAvailables', id: 'COMBO_LIST' },
      ],
    }),

    /**
     * @DELETE remove matter type
     */
    deleteMatterType: builder.mutation<MattersTypes, { profileId: number; matterTypeId: number }>({
      queryFn: async ({ profileId, matterTypeId }) => {
        try {
          const { data } = await mainApi.delete(`type-matters/${matterTypeId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'MattersTypes', id: 'LIST' },
        { type: 'MattersTypesAvailables', id: 'COMBO_LIST' },
      ],
    }),

    /**
     * @GET get all type subjects to combo box
     */
    /**
     * @GET all matters types
     */
    getSubjectTypes: builder.query<MattersTypes[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get<MattersTypes[]>('/type-matters', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'MattersTypesAvailables', id: 'COMBO_LIST' }],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMattersTypesQuery,
  useAddMatterTypeMutation,
  useDeleteMatterTypeMutation,
  useGetMatterTypeQuery,
  useUpdateMatterTypeMutation,
  useGetSubjectTypesQuery,
  useGetAvailablesMatterTypeQuery,
} = mattersTypesApi;
